import { useMemo } from 'react';
import { useTenantHasConfluence } from '@atlassian/jira-business-confluence-pages/src/utils/use-tenant-has-confluence/index.tsx';
import {
	COMMENT_SUMMARY_TYPE,
	REMOTE_ISSUE_LINKS_TYPE,
} from '@atlassian/jira-business-constants/src/index.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { getSupportedFieldTypes as getSupportedFieldTypesCommon } from '@atlassian/jira-business-fields/src/common/supported-field-types/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	CATEGORY_TYPE,
	ASSIGNEE_TYPE,
	CREATED,
	DUE_DATE_TYPE,
	ISSUE_TYPE,
	LABELS_TYPE,
	PRIORITY_TYPE,
	REPORTER_TYPE,
	SUMMARY_TYPE,
	STATUS_TYPE,
	UPDATED,
	TEXT_CF_TYPE,
	USER_CF_TYPE,
	NUMBER_CF_TYPE,
	TIME_ESTIMATE_TYPE,
	TEXT_AREA_CF_TYPE,
	DATE_CF_TYPE,
	DATETIME_CF_TYPE,
	SELECT_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	URL_CF_TYPE,
	MULTI_USER_CF_TYPE,
	COMPONENTS_TYPE,
	STORY_POINTS_TYPE,
	LABELS_CF_TYPE,
	PEOPLE_CF_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	SPRINT_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useJWMListFeatures } from '../../features-context/index.tsx';
import type { GetIssueFieldsConstants } from '../types.tsx';
import {
	ENABLED_COMMENTS_FIELD_LOCAL_STORAGE_KEY,
	isUnreadCommentsInListViewExperimentEnabled,
} from '../../../common/utils/unread-comments-in-list-view-experiment/index.tsx';
import { localStore } from '../../../common/utils/storage.tsx';
import messages from './messages.tsx';

export const DEFAULT_CUSTOM_FIELDS_BY_TYPE: string[] = [CATEGORY_TYPE, SPRINT_TYPE];

export const getInitVisibleFields = ({
	isCategoryEnabled = true,
	isScrumProject = false,
	entityId,
}: {
	isCategoryEnabled?: boolean;
	isScrumProject?: boolean;
	// Remove optionals when `jira_unread_comments_in_list_view` is cleaned up
	entityId?: string;
}): string[] => {
	const visibleFields = [
		ISSUE_TYPE,
		'key',
		SUMMARY_TYPE,
		STATUS_TYPE,
		isCategoryEnabled ? CATEGORY_TYPE : undefined,
		isScrumProject ? SPRINT_TYPE : undefined,
		ASSIGNEE_TYPE,
		DUE_DATE_TYPE,
		PRIORITY_TYPE,
		LABELS_TYPE,
		CREATED,
		UPDATED,
		REPORTER_TYPE,
	].filter(Boolean);

	if (expVal('jira_unread_comments_in_list_view', 'isEnabled', false)) {
		visibleFields.splice(4, 0, COMMENT_SUMMARY_TYPE);

		const enabledCommentsFieldStorageKey = `${entityId}_${ENABLED_COMMENTS_FIELD_LOCAL_STORAGE_KEY}`;
		localStore.set(enabledCommentsFieldStorageKey, true);
	}

	return visibleFields;
};

export const getDefaultVisibleFields = ({
	isCategoryEnabled = true,
	isScrumProject = false,
}: {
	isCategoryEnabled?: boolean;
	isScrumProject?: boolean;
}): Record<string, boolean> => ({
	[ISSUE_TYPE]: true,
	key: true,
	[SUMMARY_TYPE]: true,
	[STATUS_TYPE]: true,
	...(isUnreadCommentsInListViewExperimentEnabled() ? { [COMMENT_SUMMARY_TYPE]: true } : {}),
	...(isCategoryEnabled ? { [CATEGORY_TYPE]: true } : {}),
	...(isScrumProject ? { [SPRINT_TYPE]: true } : {}),
	[ASSIGNEE_TYPE]: true,
	[DUE_DATE_TYPE]: true,
	[PRIORITY_TYPE]: true,
	[LABELS_TYPE]: true,
	[CREATED]: true,
	[UPDATED]: true,
	[REPORTER_TYPE]: true,
});

export const getAlwaysVisibleFields = () => [ISSUE_TYPE, 'key', SUMMARY_TYPE];

export const getSupportedFieldTypes = ({
	defaultVisibleFields,
	hasConfluence = false,
	isSoftwareFieldsEnabled = false,
	isBusinessFieldsEnabled = true,
	isTeamManaged = true,
	isScrumProject = false,
}: {
	defaultVisibleFields?: Record<string, boolean>;
	hasConfluence?: boolean;
	isSoftwareFieldsEnabled?: boolean;
	isBusinessFieldsEnabled?: boolean;
	isTeamManaged?: boolean;
	isScrumProject?: boolean;
} = {}) =>
	Object.keys(
		defaultVisibleFields ??
			getDefaultVisibleFields({ isCategoryEnabled: isBusinessFieldsEnabled, isScrumProject }),
	)
		.concat([
			TEXT_CF_TYPE,
			USER_CF_TYPE,
			NUMBER_CF_TYPE,
			TIME_ESTIMATE_TYPE,
			TEXT_AREA_CF_TYPE,
			DATE_CF_TYPE,
			DATETIME_CF_TYPE,
			SELECT_CF_TYPE,
			MULTI_SELECT_CF_TYPE,
			MULTI_CHECKBOXES_CF_TYPE,
			URL_CF_TYPE,
			MULTI_USER_CF_TYPE,
			COMPONENTS_TYPE,
			LABELS_CF_TYPE,
			PEOPLE_CF_TYPE,
			...(isUnreadCommentsInListViewExperimentEnabled() ? [] : [COMMENT_SUMMARY_TYPE]),
		])
		.concat(isSoftwareFieldsEnabled && isTeamManaged ? [STORY_POINT_ESTIMATE_CF_TYPE] : [])
		.concat(isSoftwareFieldsEnabled && !isTeamManaged ? [STORY_POINTS_TYPE] : [])
		.concat(isSoftwareFieldsEnabled && !isScrumProject ? [SPRINT_TYPE] : [])
		.concat(hasConfluence ? [REMOTE_ISSUE_LINKS_TYPE] : [])
		.concat(getSupportedFieldTypesCommon(isSoftwareFieldsEnabled));

export const useMissingFields = (hasConfluence?: boolean) => {
	const { formatMessage } = useIntl();

	return useMemo(
		() => [
			{
				id: 'key',
				type: 'key',
				name: formatMessage(messages.key),
				issueTypes: [],
				fieldConfig: { isRequired: true, isEditable: false },
			},
			{
				id: COMMENT_SUMMARY_TYPE,
				type: COMMENT_SUMMARY_TYPE,
				name: formatMessage(messages.comments),
				issueTypes: [],
				fieldConfig: { isRequired: true, isEditable: false },
			},
			...(hasConfluence
				? [
						{
							id: REMOTE_ISSUE_LINKS_TYPE,
							type: REMOTE_ISSUE_LINKS_TYPE,
							name: formatMessage(messages.confluencePages),
							issueTypes: [],
							fieldConfig: { isRequired: false, isEditable: true },
						},
					]
				: []),
		],
		[formatMessage, hasConfluence],
	);
};

export const useConstants = (): GetIssueFieldsConstants => {
	const hasConfluence = useTenantHasConfluence();

	const projectData = useProject();

	const isTeamManaged = projectData.isSimplified;

	const { isSoftwareFieldsEnabled, isBusinessFieldsEnabled, isScrumProject } = useJWMListFeatures();

	const missingFields = useMissingFields(hasConfluence);

	return useMemo(() => {
		const defaultVisibleFields = getDefaultVisibleFields({
			isCategoryEnabled: isBusinessFieldsEnabled,
			isScrumProject,
		});
		const alwaysVisibleFields = getAlwaysVisibleFields();
		const supportedFieldTypes: string[] = getSupportedFieldTypes({
			defaultVisibleFields,
			hasConfluence,
			isSoftwareFieldsEnabled,
			isBusinessFieldsEnabled,
			isTeamManaged,
			isScrumProject,
		});

		return {
			DEFAULT_VISIBLE_FIELDS: defaultVisibleFields,
			ALWAYS_VISIBLE_FIELDS: alwaysVisibleFields,
			SUPPORTED_FIELD_TYPES: supportedFieldTypes,
			MISSING_FIELDS: missingFields,
		};
	}, [
		isBusinessFieldsEnabled,
		hasConfluence,
		isSoftwareFieldsEnabled,
		isTeamManaged,
		isScrumProject,
		missingFields,
	]);
};
